import { useEffect, useRef, useState } from "react";
import { X, Play, Pause, Lock, Download, ShoppingCart, Film, Music4 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PREVIEW_LIMIT = 60; // seconds — server clips 60s too but we belt-and-brace

const fmt = (s) => {
  if (!s || Number.isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};

/**
 * TeaserModal — opens a 60-sec preview for any priced content (audio or video).
 *
 * Props:
 *   open, onClose        — controlled visibility
 *   item                 — { title, description, previewUrl, posterUrl,
 *                           contentType: 'music'|'video',
 *                           priceLabel: '$4.99',
 *                           productId (linked product for buy/download),
 *                           artistName }
 *   purchaseState        — { owned: bool, downloadsUsed, downloadsRemaining, maxDownloads }
 *   token                — session token (only needed for "Download Full File")
 *   onBuyClick           — callback when the buy button is pressed (parent adds to cart)
 *   onDownloaded         — callback after a successful full-file download (parent refreshes counts)
 */
const TeaserModal = ({ open, onClose, item, purchaseState, token, onBuyClick, onDownloaded }) => {
  const mediaRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [hitLimit, setHitLimit] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPlaying(false);
    setCurrent(0);
    setHitLimit(false);
  }, [open, item?.previewUrl]);

  useEffect(() => {
    const el = mediaRef.current;
    if (!el || !open) return;
    const onTime = () => {
      setCurrent(el.currentTime);
      if (el.currentTime >= PREVIEW_LIMIT) {
        el.pause();
        el.currentTime = 0;
        setPlaying(false);
        setCurrent(0);
        setHitLimit(true);
      }
    };
    const onEnd = () => { setPlaying(false); setCurrent(0); };
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("ended", onEnd);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("ended", onEnd);
    };
  }, [open, item?.previewUrl]);

  const toggle = () => {
    const el = mediaRef.current;
    if (!el) return;
    if (playing) { el.pause(); setPlaying(false); }
    else { setHitLimit(false); el.play().catch(() => {}); setPlaying(true); }
  };

  const handleDownload = async () => {
    if (!item?.productId || !token) {
      toast.error("Sign in required to download.");
      return;
    }
    setDownloading(true);
    try {
      const { data } = await axios.get(`${API}/products/${item.productId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Open the file in a new tab (browser handles the actual download)
      const url = data.download_url?.startsWith("http")
        ? data.download_url
        : `${process.env.REACT_APP_BACKEND_URL}${data.download_url}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename || "stardomfans-download";
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`Download started — ${data.downloads_remaining} of ${data.max_downloads} left after this.`);
      onDownloaded && onDownloaded(data);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Download failed";
      toast.error(detail);
    } finally {
      setDownloading(false);
    }
  };

  if (!open || !item) return null;

  const backendBase = process.env.REACT_APP_BACKEND_URL || "";
  const src = item.previewUrl
    ? (item.previewUrl.startsWith("http") ? item.previewUrl : `${backendBase}${item.previewUrl}`)
    : null;
  const isVideo = (item.contentType === "video") || (src && (src.endsWith(".mp4") || src.endsWith(".webm") || src.endsWith(".mov")));
  const poster = item.posterUrl
    ? (item.posterUrl.startsWith("http") ? item.posterUrl : `${backendBase}${item.posterUrl}`)
    : null;

  const owned = !!purchaseState?.owned;
  const remaining = Number.isFinite(purchaseState?.downloadsRemaining) ? purchaseState.downloadsRemaining : 3;
  const used = Number.isFinite(purchaseState?.downloadsUsed) ? purchaseState.downloadsUsed : 0;
  const cap = Number.isFinite(purchaseState?.maxDownloads) ? purchaseState.maxDownloads : 3;

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/85 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-150"
      onClick={onClose}
      data-testid="teaser-modal"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative w-full max-w-2xl rounded-2xl bg-[#0A0E18] border border-[#1A2030] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center text-white"
          aria-label="Close preview"
          data-testid="teaser-close-btn"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Media */}
        <div className="relative aspect-video bg-black flex items-center justify-center">
          {isVideo ? (
            <video ref={mediaRef} src={src} poster={poster} className="w-full h-full object-contain" preload="metadata" playsInline data-testid="teaser-video" />
          ) : (
            <>
              {poster ? (
                <img src={poster} alt={item.title} className="absolute inset-0 w-full h-full object-cover opacity-60" />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-[#EC008C]/15 via-[#0A0E18] to-black" />
              )}
              <div className="relative z-[1] flex flex-col items-center gap-3 text-white">
                <div className="w-16 h-16 rounded-full bg-[#EC008C]/15 border border-[#EC008C]/30 flex items-center justify-center">
                  <Music4 className="w-7 h-7 text-[#EC008C]" />
                </div>
                <p className="text-sm text-[#8A92A0]">Audio preview</p>
              </div>
              <audio ref={mediaRef} src={src} preload="metadata" />
            </>
          )}
        </div>

        {/* Controls */}
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <button
              onClick={toggle}
              className="w-12 h-12 rounded-full bg-[#EC008C] text-black flex items-center justify-center hover:bg-[#FF3399] transition-colors flex-shrink-0 shadow-lg shadow-[#EC008C]/25"
              aria-label={playing ? "Pause preview" : "Play preview"}
              data-testid="teaser-play-toggle"
            >
              {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </button>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                {isVideo ? (
                  <Film className="w-3.5 h-3.5 text-[#EC008C]" />
                ) : (
                  <Music4 className="w-3.5 h-3.5 text-[#EC008C]" />
                )}
                <p className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold">Preview · 60 seconds</p>
              </div>
              <h3 className="text-white font-bold truncate">{item.title}</h3>
              {item.artistName && <p className="text-xs text-[#8A92A0] truncate">{item.artistName}</p>}
              <div className="mt-2">
                <div className="h-1.5 rounded-full bg-[#1A2030] overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#EC008C] to-[#FF3399] transition-[width]"
                    style={{ width: `${Math.min(100, (current / PREVIEW_LIMIT) * 100)}%` }}
                  />
                </div>
                <div className="flex justify-between text-[10px] text-[#8A92A0] mt-1">
                  <span>{fmt(current)}</span>
                  <span>1:00</span>
                </div>
              </div>
            </div>
          </div>

          {hitLimit && !owned && (
            <div className="mt-4 flex items-start gap-2.5 p-3 rounded-lg bg-[#EC008C]/[0.06] border border-[#EC008C]/25" data-testid="teaser-hit-limit">
              <Lock className="w-4 h-4 text-[#EC008C] mt-0.5 flex-shrink-0" />
              <div className="text-xs text-[#8A92A0]">
                Preview ended. Unlock the full {isVideo ? "movie" : "track"} — instant access the moment your payment is confirmed.
              </div>
            </div>
          )}

          {/* Action row */}
          <div className="mt-5">
            {owned ? (
              <>
                <button
                  disabled={downloading || remaining <= 0}
                  onClick={handleDownload}
                  className={`w-full h-12 rounded-full font-bold flex items-center justify-center gap-2 transition-colors ${remaining <= 0 ? "bg-[#1A2030] text-[#4A5260] cursor-not-allowed" : "bg-[#EC008C] text-black hover:bg-[#FF3399]"}`}
                  data-testid="teaser-download-btn"
                >
                  <Download className="w-4 h-4" />
                  {remaining <= 0
                    ? "Download limit reached"
                    : downloading
                    ? "Preparing…"
                    : `Download Full File · ${remaining} of ${cap} left`}
                </button>
                <p className="text-[11px] text-[#4A5260] text-center mt-2">
                  {used > 0
                    ? `You've used ${used} of ${cap} downloads on this purchase.`
                    : `Every buyer gets ${cap} downloads. Contact support for a top-up.`}
                </p>
              </>
            ) : (
              onBuyClick && (
                <button
                  onClick={onBuyClick}
                  className="w-full h-12 rounded-full font-bold bg-[#EC008C] text-black hover:bg-[#FF3399] flex items-center justify-center gap-2"
                  data-testid="teaser-buy-btn"
                >
                  <ShoppingCart className="w-4 h-4" />
                  Buy full {isVideo ? "movie" : "track"} {item.priceLabel ? `— ${item.priceLabel}` : ""}
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeaserModal;
